import React, { useState, useEffect, useMemo } from 'react';
import { Table, Input, InputNumber, Select, DatePicker, Button, Form, Row, Col, Space, Alert, message, Popconfirm, Card, Typography, Divider, Modal, Upload, Checkbox, Radio, Tooltip } from 'antd';
import { SearchOutlined, SaveOutlined, UndoOutlined, PlusOutlined, DeleteOutlined, EditOutlined, PaperClipOutlined, PictureOutlined, MenuOutlined, SettingOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import treeData from '../../Item/treeView.json';
import firebase from '../../../firebase';
import ProductExcelImport from './ProductExcelImport';

// Preserve column settings across localStorage.clear() on logout
if (typeof window !== 'undefined' && window.localStorage) {
    const originalClear = window.localStorage.clear;
    window.localStorage.clear = function () {
        const preserved = [];
        try {
            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (key && key.startsWith('p4cloud_pm_layout_')) {
                    preserved.push({ key, value: window.localStorage.getItem(key) });
                }
            }
        } catch (e) {
            console.error('Error preserving localStorage:', e);
        }

        originalClear.apply(this);

        try {
            preserved.forEach(item => {
                window.localStorage.setItem(item.key, item.value);
            });
        } catch (e) {
            console.error('Error restoring localStorage:', e);
        }
    };
}

const { Title, Text } = Typography;

const TAX_OPTIONS = [
    { label: '10% (通常税率)', value: 0.1 },
    { label: '8% (軽減税率)', value: 0.08 },
    { label: '0% (無税/非課税)', value: 0 }
];

const IGNORED_KEYS = ['id', 'updatedAt', 'P4CLOUD登録日', '新規登録依頼日'];

const REQUIRED_FIELDS = [
    { key: 'No', label: 'No' },
    { key: 'P4CLOUD商品名', label: 'P4CLOUD商品名' },
    { key: 'priceAmount', label: '価格 (円)' },
    { key: '売価', label: '売価 (円)' },
    { key: '正式メーカー名', label: '正式メーカー名' },
    { key: '大分類', label: '大分類' },
    { key: '中分類', label: '中分類' },
    { key: '小分類', label: '小分類' },
    { key: '荷姿名称１', label: '荷姿名称１' },
    { key: '単位', label: '単位' },
    { key: '容量・入数', label: '容量・入数' },
    { key: '発注単位', label: '発注単位' },
    { key: '発注数', label: '発注数' },
    { key: '発注数量', label: '発注数量' },
    { key: '消費税率', label: '消費税率' },
    { key: '正式メーカー商品名', label: '正式メーカー商品名' },
    { key: '仕入先（本社）', label: '仕入先（本社）' },
    { key: '仕入先事業所名', label: '仕入先事業所名' },
    { key: 'expirationDate', label: '価格有効期限' }
];

const FIELD_LABELS = {
    No: 'No',
    P4CLOUD商品名: '商品名',
    P4CLOUD商品名ふりがな: 'ふりがな',
    大分類: '大分類',
    中分類: '中分類',
    小分類: '小分類',
    荷姿名称１: '荷姿名称１',
    単位: '単位',
    '容量・入数': '容量・入数',
    発注単位: '発注単位',
    発注数: '発注数',
    発注数量: '発注数量',
    色: '色',
    容量: '容量',
    分類: '分類',
    劇毒物区分名: '劇毒物区分',
    危険物区分名: '危険物区分',
    売単価: '売単価',
    売価: '売価',
    消費税率: '消費税率',
    正式メーカー名: '正式メーカー名',
    メーカー商品コード: 'メーカー商品コード',
    正式メーカー商品名: '正式メーカー商品名',
    メーカー法人番号: 'メーカー法人番号',
    '仕入先（本社）': '仕入先（本社）',
    仕入先事業所名: '仕入先事業所名',
    仕入先事業所コード: '仕入先事業所コード',
    仕入先法人番号: '仕入先法人番号',
    仕入先EDIコード商品コード: '仕入先EDIコード',
    仕入先発注コード: '仕入先発注コード',
    販売企業商品コード: '販売企業商品コード',
    archive: 'アーカイブ',
    image: 'メイン画像'
};

// Normalize full-width Japanese alphanumeric/spaces to standard half-width ASCII
const normalizeText = (str) => {
    if (!str) return '';
    return str
        .toString()
        .replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0)) // Full-width ASCII to half-width
        .replace(/\u3000/g, ' ') // Full-width space to standard space
        .toLowerCase()
        .trim();
};

const getCollectionRef = (db, org, orgSeller) => {
    if (!db || !org || !orgSeller) return null;
    if (orgSeller.reseller && org.buyer) {
        return db.collection('items_sell').doc(orgSeller.id).collection('buyers').doc(org.id).collection('items');
    }
    if (orgSeller.maker && org.reseller) {
        return db.collection('items_stock').doc(orgSeller.id).collection('buyers').doc(org.id).collection('items');
    }
    return null;
};

// Custom hook to resolve Firebase Storage path to HTTP Download URL (Reused from ProductFormDrawer)
const useFirebaseImage = (src, storage) => {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (src && storage) {
            try {
                if (src.startsWith('gs://') || src.startsWith('http://') || src.startsWith('https://')) {
                    storage.refFromURL(src)
                        .getDownloadURL()
                        .then(setUrl)
                        .catch(() => setUrl(null));
                } else {
                    storage.ref().child(src)
                        .getDownloadURL()
                        .then(setUrl)
                        .catch(() => setUrl(null));
                }
            } catch (e) {
                setUrl(null);
            }
        } else {
            setUrl(null);
        }
    }, [src, storage]);
    return url;
};

// Product Image Thumbnail inside Grid cell
const ProductImageThumbnail = ({ src, storage, onClick, cellStyle }) => {
    const url = useFirebaseImage(src, storage);
    return (
        <div
            onClick={onClick}
            style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                overflow: 'hidden',
                backgroundColor: '#fafafa',
                margin: '0 auto',
                transition: 'border-color 0.3s',
                ...cellStyle
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#1890ff'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = cellStyle.border ? '#ffe7ba' : '#d9d9d9'}
        >
            {url ? (
                <img src={url} alt="product" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <span style={{ fontSize: '10px', color: '#ccc' }}>なし</span>
            )}
        </div>
    );
};

// Product Image Large Preview inside Modal
const ProductImageLargePreview = ({ src, storage }) => {
    const url = useFirebaseImage(src, storage);
    return (
        <div style={{
            width: '200px',
            height: '200px',
            border: '1px dashed #d9d9d9',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fafafa',
            overflow: 'hidden',
            margin: '0 auto 16px auto'
        }}>
            {url ? (
                <img src={url} alt="product preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
                <span style={{ color: '#999' }}>未登録 (No Image)</span>
            )}
        </div>
    );
};

// Individual File Attachment Item Component (Reused from ProductFormDrawer)
const FileAttachmentItem = ({ fileId, name, onRename, onDelete, storage }) => {
    const [downloadUrl, setDownloadUrl] = useState(null);
    const [fileName, setFileName] = useState(name);
    const [isEditingName, setIsEditingName] = useState(false);

    useEffect(() => {
        if (fileId && storage) {
            const db = firebase.firestore();
            db.collection('files').doc(fileId).get()
                .then(doc => {
                    if (doc.exists) {
                        const fileData = doc.data();
                        if (fileData && fileData.file) {
                            storage.ref(fileData.file).getDownloadURL()
                                .then(setDownloadUrl)
                                .catch(err => console.error('Error resolving download url:', err));
                        }
                    }
                })
                .catch(err => console.error('Error fetching file doc:', err));
        }
    }, [fileId, storage]);

    useEffect(() => {
        setFileName(name);
    }, [name]);

    const handleSaveName = () => {
        setIsEditingName(false);
        if (fileName.trim() && fileName !== name) {
            onRename(fileName.trim());
        }
    };

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#fafafa',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #d9d9d9',
            marginBottom: 8
        }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                {isEditingName ? (
                    <Input
                        size="small"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        onBlur={handleSaveName}
                        onPressEnter={handleSaveName}
                        style={{ width: '70%' }}
                        autoFocus
                    />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span
                            style={{ fontWeight: 500, cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => setIsEditingName(true)}
                            title="クリックで名前を編集"
                        >
                            {name}
                        </span>
                        {downloadUrl && (
                            <a href={downloadUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#1890ff' }}>
                                ダウンロード / 表示
                            </a>
                        )}
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                {!isEditingName && (
                    <Button size="small" type="link" onClick={() => setIsEditingName(true)}>
                        名前変更
                    </Button>
                )}
                <Button size="small" type="link" danger onClick={onDelete}>
                    削除
                </Button>
            </div>
        </div>
    );
};

const DEFAULT_COLUMN_ORDER = [
    'No', 'image', 'P4CLOUD商品名', 'priceAmount', '売価', '正式メーカー名', '大分類', '中分類', '小分類',
    '荷姿名称１', '単位', '容量・入数', '発注単位', '発注数', '発注数量', '消費税率', '正式メーカー商品名',
    '仕入先（本社）', '仕入先事業所名', 'P4CLOUD商品名ふりがな', '色', '容量', '分類', '劇毒物区分名',
    '危険物区分名', '取引日', 'expirationDate', 'archive', 'data', 'files', '売単価', 'メーカー商品コード',
    'メーカー法人番号', '仕入先事業所コード', '仕入先法人番号', '仕入先EDIコード商品コード', '仕入先発注コード',
    '販売企業商品コード'
];

const ProductManagement = ({ db, storage, org, orgSeller, user }) => {
    // items holds baseline from firestore, localItems holds active modifications
    const [items, setItems] = useState([]);
    const [localItems, setLocalItems] = useState([]);
    const [insertedRows, setInsertedRows] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');

    // Inline edit state
    const [editingCell, setEditingCell] = useState(null); // { rowId, colKey }
    const [editValue, setEditValue] = useState(null);

    // Data array editor state
    const [dataEditorOpen, setDataEditorOpen] = useState(false);
    const [dataEditingRecordId, setDataEditingRecordId] = useState(null);
    const [editingDataArray, setEditingDataArray] = useState([]); // [{ title, value }]

    // Files/Attachments editor state
    const [filesEditorOpen, setFilesEditorOpen] = useState(false);
    const [filesEditingRecordId, setFilesEditingRecordId] = useState(null);
    const [editingFilesArray, setEditingFilesArray] = useState([]); // [{ file, name }]
    const [uploadingFile, setUploadingFile] = useState(false);

    // Image editor state
    const [imageEditorOpen, setImageEditorOpen] = useState(false);
    const [imageEditingRecordId, setImageEditingRecordId] = useState(null);
    const [editingImageSrc, setEditingImageSrc] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    // Save changes confirmation modal state
    const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
    const [changeSummary, setChangeSummary] = useState({ inserted: [], modified: [] });

    // Custom layout settings states
    const [columnOrder, setColumnOrder] = useState(DEFAULT_COLUMN_ORDER);
    const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMN_ORDER);

    // View filter tabs states
    const [currentTab, setCurrentTab] = useState('active'); // 'active', 'archived', 'expired'
    const [expiredDateRange, setExpiredDateRange] = useState(null);

    // Reset page to 1 on tab, search text, or date range change
    useEffect(() => {
        setCurrentPage(1);
    }, [currentTab, searchText, expiredDateRange]);

    // Bulk selection & bulk edit states
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkEditOpen, setBulkEditOpen] = useState(false);
    const [bulkEditField, setBulkEditField] = useState('');
    const [bulkEditValue, setBulkEditValue] = useState(null);

    // Column settings modal state
    const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);

    // Column widths state for resizing
    const [colWidths, setColWidths] = useState({
        No: 80,
        P4CLOUD商品名: 250,
        image: 100,
        priceAmount: 120,
        売価: 120,
        正式メーカー名: 180,
        大分類: 120,
        中分類: 120,
        小分類: 120,
        荷姿名称１: 120,
        単位: 100,
        '容量・入数': 120,
        発注単位: 120,
        発注数: 100,
        発注数量: 100,
        消費税率: 120,
        正式メーカー商品名: 200,
        '仕入先（本社）': 180,
        仕入先事業所名: 180,
        P4CLOUD商品名ふりがな: 200,
        色: 100,
        容量: 100,
        分類: 120,
        劇毒物区分名: 120,
        危険物区分名: 120,
        取引日: 140,
        expirationDate: 140,
        archive: 140,
        data: 160,
        files: 160,
        売単価: 120,
        メーカー商品コード: 150,
        メーカー法人番号: 150,
        仕入先事業所コード: 150,
        仕入先法人番号: 150,
        仕入先EDIコード商品コード: 180,
        仕入先発注コード: 150,
        販売企業商品コード: 150
    });

    const getLayoutKey = () => {
        const keyId = user?.id || user?.uid;
        return keyId ? `p4cloud_pm_layout_${keyId}` : `p4cloud_pm_layout_default`;
    };

    useEffect(() => {
        // 1. Try loading from Firestore user document prop first
        if (user?.pm_layout) {
            const parsed = user.pm_layout;
            if (parsed.order) setColumnOrder(parsed.order);
            if (parsed.visible) setVisibleColumns(parsed.visible);
            if (parsed.widths) setColWidths(parsed.widths);

            // Sync to local storage
            const key = getLayoutKey();
            localStorage.setItem(key, JSON.stringify(parsed));
            return;
        }

        // 2. Fallback to localStorage
        const key = getLayoutKey();
        const saved = localStorage.getItem(key);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed.order) setColumnOrder(parsed.order);
                if (parsed.visible) setVisibleColumns(parsed.visible);
                if (parsed.widths) setColWidths(parsed.widths);
            } catch (e) {
                console.error('Failed to load saved column layout:', e);
            }
        }
    }, [user]);

    const saveLayout = async (order, visible, widths) => {
        const layout = { order, visible, widths };
        const key = getLayoutKey();

        // Save locally
        localStorage.setItem(key, JSON.stringify(layout));

        // Save to Firestore so it persists across logouts (since logout clears localStorage)
        if (db && user?.id) {
            try {
                await db.collection('users').doc(user.id).update({
                    pm_layout: layout
                });
            } catch (e) {
                console.error('Failed to save layout to Firestore:', e);
            }
        }
    };

    const fetchItems = async () => {
        const ref = getCollectionRef(db, org, orgSeller);
        if (!ref) {
            setItems([]);
            setLocalItems([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const snapshot = await ref.get();
            const list = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            list.sort((a, b) => Number(a.No || 0) - Number(b.No || 0));
            setItems(list);
            setLocalItems(list.map(item => ({ ...item }))); // Shallow map preserves Timestamp objects
            setInsertedRows([]);
        } catch (err) {
            console.error(err);
            message.error('商品データの取得に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    // Load products on mount/org changes
    useEffect(() => {
        fetchItems();
    }, [db, org, orgSeller]);

    // Handle column resizing drag
    const handleResize = (key) => (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = colWidths[key] || 120;

        const doDrag = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            setColWidths(prev => ({
                ...prev,
                [key]: Math.max(50, startWidth + deltaX) // Min width 50px
            }));
        };

        const stopDrag = () => {
            document.removeEventListener('mousemove', doDrag);
            document.removeEventListener('mouseup', stopDrag);
            setColWidths(currentWidths => {
                saveLayout(columnOrder, visibleColumns, currentWidths);
                return currentWidths;
            });
        };

        document.addEventListener('mousemove', doDrag);
        document.addEventListener('mouseup', stopDrag);
    };

    const handleRowMove = (dragIndex, hoverIndex) => {
        const itemsCopy = [...filteredItems];
        const [movedItem] = itemsCopy.splice(dragIndex, 1);
        itemsCopy.splice(hoverIndex, 0, movedItem);

        // Update No based on the new order
        const updated = itemsCopy.map((item, idx) => ({
            ...item,
            No: idx + 1
        }));

        // Now separate them back into insertedRows and localItems
        const newInserted = updated.filter(item => item.isNew || item.id.toString().startsWith('new_row_'));
        const newLocal = updated.filter(item => !item.isNew && !item.id.toString().startsWith('new_row_'));

        setInsertedRows(newInserted);
        setLocalItems(newLocal);
    };

    const handleColumnDragStart = (e, index) => {
        e.dataTransfer.setData('columnIndex', index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleColumnDrop = (e, targetIndex) => {
        const sourceIndex = Number(e.dataTransfer.getData('columnIndex'));
        if (sourceIndex === targetIndex) return;
        const newOrder = [...columnOrder];
        const [moved] = newOrder.splice(sourceIndex, 1);
        newOrder.splice(targetIndex, 0, moved);
        setColumnOrder(newOrder);
        saveLayout(newOrder, visibleColumns, colWidths);
    };

    const handleApplyBulkEdit = () => {
        if (!bulkEditField) {
            message.warning('一括編集する項目を選択してください。');
            return;
        }

        const updater = (prevRows) => prevRows.map(row => {
            if (selectedRowKeys.includes(row.id)) {
                const updatedRow = { ...row };
                const newValue = bulkEditValue;

                if (bulkEditField === 'priceAmount') {
                    const currentPriceArray = row.price || [];
                    const priceVal = newValue !== null && newValue !== undefined ? Number(newValue) : 0;
                    const origExp = currentPriceArray[0]?.expiration_date;
                    let dateObj = null;
                    if (origExp) {
                        dateObj = typeof origExp.toDate === 'function' ? origExp.toDate() : new Date(origExp);
                    }
                    const expVal = !dateObj || isNaN(dateObj.getTime()) ? new Date('2099-12-01') : dateObj;
                    updatedRow.price = [{ price: priceVal, expiration_date: expVal }];
                } else if (bulkEditField === 'expirationDate') {
                    const currentPriceArray = row.price || [];
                    const priceVal = currentPriceArray[0]?.price || 0;
                    let expVal;
                    if (newValue) {
                        const dateObj = new Date(newValue);
                        expVal = isNaN(dateObj.getTime()) ? new Date('2099-12-01') : dateObj;
                    } else {
                        expVal = new Date('2099-12-01');
                    }
                    updatedRow.price = [{ price: priceVal, expiration_date: expVal }];
                } else if (bulkEditField === '取引日') {
                    const dateObj = newValue ? new Date(newValue) : null;
                    updatedRow['取引日'] = dateObj && !isNaN(dateObj.getTime()) ? dateObj : null;
                } else if (bulkEditField === 'archive') {
                    updatedRow.archive = newValue === true || newValue === 'true' || newValue === 'archive';
                } else if (['No', '発注数', '発注数量', '売単価', '売価', '消費税率'].includes(bulkEditField)) {
                    updatedRow[bulkEditField] = newValue !== null && newValue !== '' ? Number(newValue) : null;
                } else {
                    updatedRow[bulkEditField] = (newValue !== null && newValue !== undefined && newValue !== '') ? newValue.toString().trim() : null;
                }
                return updatedRow;
            }
            return row;
        });

        // Apply edits to both localItems and insertedRows drafts
        setInsertedRows(updater);
        setLocalItems(updater);

        setBulkEditOpen(false);
        setBulkEditValue(null);
        setBulkEditField('');
        setSelectedRowKeys([]);
        message.success(`${selectedRowKeys.length}件の選択行に対して値を更新しました（保存するまで変更は確定されません）。`);
    };

    const handleCellClick = (record, colKey, currentValue) => {
        setEditingCell({ rowId: record.id, colKey });
        setEditValue(currentValue);
    };

    const handleLocalCellSave = (recordId, colKey, newValue) => {
        const updater = (prevRows) => prevRows.map(row => {
            if (row.id === recordId) {
                const updatedRow = { ...row };
                if (colKey === 'priceAmount' || colKey === 'expirationDate') {
                    const currentPriceArray = row.price || [];
                    const priceVal = colKey === 'priceAmount' ? Number(newValue) : (currentPriceArray[0]?.price || 0);

                    let expVal;
                    if (colKey === 'expirationDate') {
                        const dateObj = newValue ? new Date(newValue) : new Date('2099-12-01');
                        expVal = isNaN(dateObj.getTime()) ? new Date('2099-12-01') : dateObj;
                    } else {
                        const origExp = currentPriceArray[0]?.expiration_date;
                        let dateObj = null;
                        if (origExp) {
                            dateObj = typeof origExp.toDate === 'function' ? origExp.toDate() : new Date(origExp);
                        }
                        expVal = !dateObj || isNaN(dateObj.getTime()) ? new Date('2099-12-01') : dateObj;
                    }

                    updatedRow.price = [{ price: priceVal, expiration_date: expVal }];
                } else if (colKey === '取引日') {
                    const dateObj = newValue ? new Date(newValue) : null;
                    updatedRow['取引日'] = dateObj && !isNaN(dateObj.getTime()) ? dateObj : null;
                } else if (colKey === 'archive') {
                    updatedRow.archive = newValue === true;
                } else if (['No', '発注数', '発注数量', '売単価', '売価', '消費税率'].includes(colKey)) {
                    updatedRow[colKey] = newValue !== null && newValue !== '' ? Number(newValue) : null;
                } else {
                    // Safe string parsing checks undefined/null values to prevent crashes
                    updatedRow[colKey] = (newValue !== null && newValue !== undefined && newValue !== '') ? newValue.toString().trim() : null;
                }
                return updatedRow;
            }
            return row;
        });

        // Save modifications locally
        if (insertedRows.some(x => x.id === recordId)) {
            setInsertedRows(updater);
        } else {
            setLocalItems(updater);
        }
        setEditingCell(null);
    };

    const handleAddRow = () => {
        const tempId = `new_row_${Date.now()}`;
        const newRow = {
            id: tempId,
            isNew: true,
            No: null,
            P4CLOUD商品名: '',
            P4CLOUD商品名ふりがな: '',
            大分類: '',
            中分類: '',
            小分類: '',
            荷姿名称１: '',
            単位: '',
            '容量・入数': '',
            発注単位: '',
            発注数: 0,
            発注数量: 0,
            色: '',
            容量: '',
            分類: '',
            劇毒物区分名: '該当外',
            危険物区分名: '該当外',
            取引日: null,
            price: [{ price: 0, expiration_date: new Date('2099-12-01') }],
            archive: false,
            data: [],
            files: [],
            files_names: [],
            image: null,
            売単価: 0,
            売価: 0,
            消費税率: 0.1,
            正式メーカー名: '',
            メーカー商品コード: '',
            正式メーカー商品名: '',
            メーカー法人番号: '',
            '仕入先（本社）': '',
            仕入先事業所名: '',
            仕入先事業所コード: '',
            仕入先法人番号: '',
            仕入先EDIコード商品コード: '',
            仕入先発注コード: '',
            販売企業商品コード: ''
        };
        setInsertedRows([newRow, ...insertedRows]); // Add new rows at the top!
        setEditingCell({ rowId: tempId, colKey: 'P4CLOUD商品名' });
        setEditValue('');
    };

    // Validate a single row for required fields
    const validateRow = (row, rowIndex, isNew) => {
        const errors = [];
        const rowNumDisplay = isNew ? `新規追加された行 (${rowIndex + 1}番目)` : `行 No.${row.No || '?'}`;

        REQUIRED_FIELDS.forEach(field => {
            let val = null;
            if (field.key === 'priceAmount') {
                val = row.price && row.price[0] ? row.price[0].price : null;
            } else if (field.key === 'expirationDate') {
                val = row.price && row.price[0] ? row.price[0].expiration_date : null;
            } else {
                val = row[field.key];
            }

            if (val === null || val === undefined || val === '') {
                errors.push(`${field.label}`);
            }
        });

        return errors.length > 0 ? { rowNumDisplay, errors } : null;
    };

    // Prepare confirmation summary list
    const getChangeSummary = () => {
        const summary = {
            inserted: [],
            modified: []
        };

        insertedRows.forEach(row => {
            summary.inserted.push({
                No: row.No,
                name: row.P4CLOUD商品名 || '未設定'
            });
        });

        localItems.forEach(row => {
            const original = items.find(x => x.id === row.id);
            if (!original) return;

            const changes = [];

            Object.keys(row).forEach(key => {
                if (IGNORED_KEYS.includes(key)) return;

                if (key === 'price') {
                    const origPrice = original.price && original.price[0] ? original.price[0].price : null;
                    const currentPrice = row.price && row.price[0] ? row.price[0].price : null;

                    const origExp = original.price && original.price[0] ? original.price[0].expiration_date : null;
                    const currentExp = row.price && row.price[0] ? row.price[0].expiration_date : null;

                    const origTime = origExp ? (typeof origExp.toDate === 'function' ? origExp.toDate().getTime() : new Date(origExp).getTime()) : null;
                    const currentTime = currentExp ? (typeof currentExp.toDate === 'function' ? currentExp.toDate().getTime() : new Date(currentExp).getTime()) : null;

                    if (origPrice !== currentPrice) {
                        changes.push({
                            label: '価格',
                            old: origPrice ? `${origPrice.toLocaleString()} 円` : '未設定',
                            new: currentPrice ? `${currentPrice.toLocaleString()} 円` : '未設定'
                        });
                    }
                    if (origTime !== currentTime) {
                        const oldDateStr = origExp ? dayjs(typeof origExp.toDate === 'function' ? origExp.toDate() : new Date(origExp)).format('YYYY-MM-DD') : '未設定';
                        const newDateStr = currentExp ? dayjs(new Date(currentExp)).format('YYYY-MM-DD') : '未設定';
                        changes.push({
                            label: '価格有効期限',
                            old: oldDateStr,
                            new: newDateStr
                        });
                    }
                } else if (key === '取引日') {
                    const origDate = original['取引日'];
                    const currentDate = row['取引日'];

                    const origTime = origDate ? (typeof origDate.toDate === 'function' ? origDate.toDate().getTime() : new Date(origDate).getTime()) : null;
                    const currentTime = currentDate ? (typeof currentDate.toDate === 'function' ? currentDate.toDate().getTime() : new Date(currentDate).getTime()) : null;

                    if (origTime !== currentTime) {
                        const oldDateStr = origDate ? dayjs(typeof origDate.toDate === 'function' ? origDate.toDate().getTime() : new Date(origDate)).format('YYYY-MM-DD') : '未設定';
                        const newDateStr = currentDate ? dayjs(new Date(currentDate)).format('YYYY-MM-DD') : '未設定';
                        changes.push({
                            label: '取引日',
                            old: oldDateStr,
                            new: newDateStr
                        });
                    }
                } else if (key === 'data') {
                    const origData = original.data || [];
                    const currentData = row.data || [];
                    if (JSON.stringify(origData) !== JSON.stringify(currentData)) {
                        changes.push({
                            label: '仕様データ',
                            old: `${origData.length} 件`,
                            new: `${currentData.length} 件`
                        });
                    }
                } else if (key === 'files') {
                    const origFiles = original.files || [];
                    const currentFiles = row.files || [];
                    if (JSON.stringify(origFiles) !== JSON.stringify(currentFiles)) {
                        changes.push({
                            label: '添付ファイル',
                            old: `${origFiles.length} 件`,
                            new: `${currentFiles.length} 件`
                        });
                    }
                } else if (original[key] !== row[key]) {
                    changes.push({
                        label: FIELD_LABELS[key] || key,
                        old: original[key] !== null && original[key] !== undefined && original[key] !== '' ? String(original[key]) : '未設定',
                        new: row[key] !== null && row[key] !== undefined && row[key] !== '' ? String(row[key]) : '未設定'
                    });
                }
            });

            if (changes.length > 0) {
                summary.modified.push({
                    No: row.No,
                    name: row.P4CLOUD商品名 || '未設定',
                    changes
                });
            }
        });

        return summary;
    };

    // Trigger changes verification step
    const handleTriggerSave = () => {
        const validationFailures = [];

        // Validate presence and duplicate of No across all items
        const activeRows = [...insertedRows, ...localItems];
        const noCounts = {};
        const duplicateNos = new Set();
        const missingNoRowNames = [];

        activeRows.forEach((row) => {
            if (row.No === null || row.No === undefined || row.No === '') {
                missingNoRowNames.push(row.P4CLOUD商品名 || '商品名未設定');
            } else {
                const num = Number(row.No);
                noCounts[num] = (noCounts[num] || 0) + 1;
                if (noCounts[num] > 1) {
                    duplicateNos.add(num);
                }
            }
        });

        insertedRows.forEach((row, idx) => {
            const errs = validateRow(row, idx, true);
            if (errs) validationFailures.push(errs);
        });

        localItems.forEach((row) => {
            const original = items.find(x => x.id === row.id);
            if (!original) return;

            let isModified = false;
            Object.keys(row).forEach(key => {
                if (IGNORED_KEYS.includes(key)) return;

                if (key === 'price') {
                    const origPrice = original.price && original.price[0] ? original.price[0].price : null;
                    const currentPrice = row.price && row.price[0] ? row.price[0].price : null;
                    const origExp = original.price && original.price[0] ? original.price[0].expiration_date : null;
                    const currentExp = row.price && row.price[0] ? row.price[0].expiration_date : null;
                    const origTime = origExp ? (typeof origExp.toDate === 'function' ? origExp.toDate().getTime() : new Date(origExp).getTime()) : null;
                    const currentTime = currentExp ? (typeof currentExp.toDate === 'function' ? currentExp.toDate().getTime() : new Date(currentExp).getTime()) : null;

                    if (origPrice !== currentPrice || origTime !== currentTime) isModified = true;
                } else if (key === '取引日') {
                    const origDate = original['取引日'];
                    const currentDate = row['取引日'];
                    const origTime = origDate ? (typeof origDate.toDate === 'function' ? origDate.toDate().getTime() : new Date(origDate).getTime()) : null;
                    const currentTime = currentDate ? (typeof currentDate.toDate === 'function' ? currentDate.toDate().getTime() : new Date(currentDate).getTime()) : null;

                    if (origTime !== currentTime) isModified = true;
                } else if (key === 'data') {
                    const origData = original.data || [];
                    const currentData = row.data || [];
                    if (JSON.stringify(origData) !== JSON.stringify(currentData)) isModified = true;
                } else if (key === 'files') {
                    const origFiles = original.files || [];
                    const currentFiles = row.files || [];
                    if (JSON.stringify(origFiles) !== JSON.stringify(currentFiles)) isModified = true;
                } else if (original[key] !== row[key]) {
                    isModified = true;
                }
            });

            if (isModified) {
                const errs = validateRow(row, 0, false);
                if (errs) validationFailures.push(errs);
            }
        });

        if (missingNoRowNames.length > 0 || duplicateNos.size > 0 || validationFailures.length > 0) {
            Modal.error({
                title: '入力値チェックエラー',
                content: (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {missingNoRowNames.length > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: '#ff4d4f' }}>「No.」が入力されていない商品があります。</Text>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    {missingNoRowNames.map((name, idx) => (
                                        <li key={idx} style={{ color: '#ff4d4f' }}>{name} の「No.」を指定してください</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {duplicateNos.size > 0 && (
                            <div style={{ marginBottom: 12 }}>
                                <Text strong style={{ color: '#ff4d4f' }}>重複している「No.」が存在します。</Text>
                                <p style={{ color: '#ff4d4f', margin: '4px 0 0 20px' }}>
                                    重複しているNo: {Array.from(duplicateNos).join(', ')}
                                </p>
                            </div>
                        )}
                        {validationFailures.length > 0 && (
                            <div>
                                <p>保存する前に、以下の必須項目をすべて入力してください。</p>
                                {validationFailures.map((failure, idx) => (
                                    <div key={idx} style={{ marginBottom: 12 }}>
                                        <Text strong>{failure.rowNumDisplay}:</Text>
                                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                                            {failure.errors.map((err, errIdx) => (
                                                <li key={errIdx} style={{ color: '#ff4d4f' }}>{err} が未入力です</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ),
                width: 500
            });
            return;
        }

        const summary = getChangeSummary();
        if (summary.inserted.length === 0 && summary.modified.length === 0) {
            message.info('保存する変更はありません。');
            return;
        }

        setChangeSummary(summary);
        setConfirmSaveOpen(true);
    };

    const handleSaveChanges = async () => {
        setConfirmSaveOpen(false);
        const ref = getCollectionRef(db, org, orgSeller);
        if (!ref) {
            message.error('保存先コレクションパスが見つかりませんでした。');
            return;
        }

        setLoading(true);
        try {
            const batch = db.batch();
            const now = new Date();
            let writeCount = 0;

            // 1. Save inserted rows
            insertedRows.forEach(row => {
                const docRef = ref.doc();

                let expVal = new Date('2099-12-01');
                if (row.price && row.price[0]?.expiration_date) {
                    const dObj = new Date(row.price[0].expiration_date);
                    if (!isNaN(dObj.getTime())) expVal = dObj;
                }
                const priceVal = row.price && row.price[0]?.price ? Number(row.price[0].price) : 0;

                const payload = {
                    No: Number(row.No),
                    P4CLOUD商品名: row.P4CLOUD商品名 || '',
                    P4CLOUD商品名ふりがな: row.P4CLOUD商品名ふりがな || null,
                    大分類: row.大分類 || '',
                    中分類: row.中分類 || '',
                    小分類: row.小分類 || '',
                    荷姿名称１: row.荷姿名称１ || '',
                    単位: row.単位 || '',
                    '容量・入数': row['容量・入数'] || '',
                    発注単位: row.発注単位 || '',
                    発注数: Number(row.発注数 || 0),
                    発注数量: Number(row.発注数量 || 0),
                    色: row.色 || null,
                    容量: row.容量 || null,
                    分類: row.分類 || null,
                    劇毒物区分名: row.劇毒物区分名 || null,
                    危険物区分名: row.危険物区分名 || null,
                    取引日: row.取引日 || null,
                    price: [{ price: priceVal, expiration_date: expVal }],
                    archive: row.archive === true,
                    data: row.data || [],
                    files: row.files || [],
                    files_names: row.files_names || [],
                    image: row.image || null,
                    売単価: row.売単価 !== null ? Number(row.売単価) : null,
                    売価: row.売価 !== null ? Number(row.売価) : 0,
                    消費税率: row.消費税率 !== null ? Number(row.消費税率) : 0.1,
                    正式メーカー名: row.正式メーカー名 || '',
                    メーカー商品コード: row.メーカー商品コード || null,
                    正式メーカー商品名: row.正式メーカー商品名 || '',
                    メーカー法人番号: row.メーカー法人番号 || null,
                    '仕入先（本社）': row['仕入先（本社）'] || '',
                    仕入先事業所名: row.仕入先事業所名 || '',
                    仕入先事業所コード: row.仕入先事業所コード || null,
                    仕入先法人番号: row.仕入先法人番号 || null,
                    仕入先EDIコード商品コード: row.仕入先EDIコード商品コード || null,
                    仕入先発注コード: row.仕入先発注コード || null,
                    販売企業商品コード: row.販売企業商品コード || null,
                    P4CLOUD登録日: now,
                    新規登録依頼日: now,
                    updatedAt: now
                };
                batch.set(docRef, payload);
                writeCount++;
            });

            // 2. Save modified rows
            localItems.forEach(row => {
                const original = items.find(x => x.id === row.id);
                if (!original) return;

                let isModified = false;
                const diff = {};

                Object.keys(row).forEach(key => {
                    if (IGNORED_KEYS.includes(key)) return;

                    if (key === 'price') {
                        const origPrice = original.price && original.price[0] ? original.price[0].price : null;
                        const currentPrice = row.price && row.price[0] ? row.price[0].price : null;

                        const origExp = original.price && original.price[0] ? original.price[0].expiration_date : null;
                        const currentExp = row.price && row.price[0] ? row.price[0].expiration_date : null;

                        const origTime = origExp ? (typeof origExp.toDate === 'function' ? origExp.toDate().getTime() : new Date(origExp).getTime()) : null;
                        const currentTime = currentExp ? (typeof currentExp.toDate === 'function' ? currentExp.toDate().getTime() : new Date(currentExp).getTime()) : null;

                        if (origPrice !== currentPrice || origTime !== currentTime) {
                            isModified = true;
                            diff.price = row.price;
                        }
                    } else if (key === '取引日') {
                        const origDate = original['取引日'];
                        const currentDate = row['取引日'];

                        const origTime = origDate ? (typeof origDate.toDate === 'function' ? origDate.toDate().getTime() : new Date(origDate).getTime()) : null;
                        const currentTime = currentDate ? (typeof currentDate.toDate === 'function' ? currentDate.toDate().getTime() : new Date(currentDate).getTime()) : null;

                        if (origTime !== currentTime) {
                            isModified = true;
                            diff['取引日'] = row['取引日'];
                        }
                    } else if (key === 'data') {
                        const origData = original.data || [];
                        const currentData = row.data || [];
                        if (JSON.stringify(origData) !== JSON.stringify(currentData)) {
                            isModified = true;
                            diff.data = row.data;
                        }
                    } else if (key === 'files') {
                        const origFiles = original.files || [];
                        const currentFiles = row.files || [];
                        if (JSON.stringify(origFiles) !== JSON.stringify(currentFiles)) {
                            isModified = true;
                            diff.files = row.files;
                            diff.files_names = row.files_names || [];
                        }
                    } else if (original[key] !== row[key]) {
                        isModified = true;
                        diff[key] = row[key];
                    }
                });

                if (isModified) {
                    diff.updatedAt = now;
                    batch.set(ref.doc(row.id), diff, { merge: true });
                    writeCount++;
                }
            });

            if (writeCount > 0) {
                await batch.commit();
                message.success(`${writeCount}件の変更を保存しました。`);
            } else {
                message.info('保存する変更はありません。');
            }

            await fetchItems();
        } catch (err) {
            console.error(err);
            message.error('変更の保存に失敗しました。');
            setLoading(false);
        }
    };

    const handleDiscardChanges = () => {
        setLocalItems(items.map(item => ({ ...item }))); // Revert local state to baseline
        setInsertedRows([]); // Clear new rows
        setEditingCell(null);
        message.info('変更を破棄しました。');
    };

    const handleDeleteRow = async (recordId) => {
        if (recordId.startsWith('new_row_')) {
            setInsertedRows(prev => prev.filter(x => x.id !== recordId));
            message.success('追加した行を削除しました。');
            return;
        }

        const ref = getCollectionRef(db, org, orgSeller);
        if (!ref) return;

        setLoading(true);
        try {
            await ref.doc(recordId).delete();
            message.success('商品を削除しました。');
            await fetchItems();
        } catch (err) {
            console.error(err);
            message.error('削除に失敗しました。');
            setLoading(false);
        }
    };

    // Filter items based on tab status, expired pricing search, and search text (width-normalized and case-insensitive)
    const filteredItems = useMemo(() => {
        let tableData = [...insertedRows, ...localItems]; // insertedRows float to the top!

        // Apply Tab Filter
        if (currentTab === 'active') {
            tableData = tableData.filter(item => item.archive !== true);
        } else if (currentTab === 'archived') {
            tableData = tableData.filter(item => item.archive === true);
        } else if (currentTab === 'expired') {
            tableData = tableData.filter(item => {
                const rawExp = item.price && item.price[0]?.expiration_date;
                let dateObj = null;
                if (rawExp) {
                    dateObj = typeof rawExp.toDate === 'function' ? rawExp.toDate() : new Date(rawExp);
                }
                const isExpired = dateObj && !isNaN(dateObj.getTime()) && dateObj < new Date();

                if (!isExpired) return false;

                if (expiredDateRange && expiredDateRange[0] && expiredDateRange[1]) {
                    const start = expiredDateRange[0].startOf('day').toDate();
                    const end = expiredDateRange[1].endOf('day').toDate();
                    return dateObj >= start && dateObj <= end;
                }
                return true;
            });
        }

        if (!searchText.trim()) return tableData;
        const q = normalizeText(searchText);
        return tableData.filter(item => {
            const name = normalizeText(item.P4CLOUD商品名);
            const maker = normalizeText(item.正式メーカー名);
            const code = normalizeText(item.メーカー商品コード);
            return name.includes(q) || maker.includes(q) || code.includes(q);
        });
    }, [localItems, insertedRows, searchText, currentTab, expiredDateRange]);

    // Detect if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (insertedRows.length > 0) return true;
        if (localItems.length !== items.length) return true;

        for (let i = 0; i < localItems.length; i++) {
            const row = localItems[i];
            const original = items.find(x => x.id === row.id);
            if (!original) continue;

            for (const key of Object.keys(row)) {
                if (IGNORED_KEYS.includes(key)) continue;

                if (key === 'price') {
                    const origPrice = original.price && original.price[0] ? original.price[0].price : null;
                    const currentPrice = row.price && row.price[0] ? row.price[0].price : null;

                    const origExp = original.price && original.price[0] ? original.price[0].expiration_date : null;
                    const currentExp = row.price && row.price[0] ? row.price[0].expiration_date : null;

                    const origTime = origExp
                        ? (typeof origExp.toDate === 'function' ? origExp.toDate().getTime() : new Date(origExp).getTime())
                        : null;
                    const currentTime = currentExp
                        ? (typeof currentExp.toDate === 'function' ? currentExp.toDate().getTime() : new Date(currentExp).getTime())
                        : null;

                    const oTime = isNaN(origTime) ? new Date('2099-12-01').getTime() : origTime;
                    const cTime = isNaN(currentTime) ? new Date('2099-12-01').getTime() : currentTime;

                    if (origPrice !== currentPrice || oTime !== cTime) return true;
                } else if (key === '取引日') {
                    const origDate = original['取引日'];
                    const currentDate = row['取引日'];

                    const origTime = origDate
                        ? (typeof origDate.toDate === 'function' ? origDate.toDate().getTime() : new Date(origDate).getTime())
                        : null;
                    const currentTime = currentDate
                        ? (typeof currentDate.toDate === 'function' ? currentDate.toDate().getTime() : new Date(currentDate).getTime())
                        : null;

                    const oTime = isNaN(origTime) ? null : origTime;
                    const cTime = isNaN(currentTime) ? null : currentTime;

                    if (origTime !== currentTime && oTime !== cTime) return true;
                } else if (key === 'data') {
                    const origData = original.data || [];
                    const currentData = row.data || [];
                    if (JSON.stringify(origData) !== JSON.stringify(currentData)) return true;
                } else if (key === 'files') {
                    const origFiles = original.files || [];
                    const currentFiles = row.files || [];
                    if (JSON.stringify(origFiles) !== JSON.stringify(currentFiles)) return true;
                } else if (original[key] !== row[key]) {
                    return true;
                }
            }
        }
        return false;
    }, [items, localItems, insertedRows]);

    // Categories Options
    const majorOptions = useMemo(() => treeData.filter(item => item.level === 'major').map(item => ({ label: item.name2, value: item.code })), []);

    const getMiddleOptions = (majorCode) => {
        const majorItem = treeData.find(item => item.level === 'major' && item.code === majorCode);
        return majorItem
            ? treeData.filter(item => item.level === 'middle' && item.categoryId === majorItem.ID).map(item => ({ label: item.name2, value: item.code }))
            : [];
    };

    const getSmallOptions = (majorCode, middleCode) => {
        const majorItem = treeData.find(item => item.level === 'major' && item.code === majorCode);
        const middleItem = majorItem ? treeData.find(item => item.level === 'middle' && item.code === middleCode && item.categoryId === majorItem.ID) : null;
        return middleItem
            ? treeData.filter(item => item.level === 'small' && item.categoryId === middleItem.ID).map(item => ({ label: item.name2, value: item.code }))
            : [];
    };

    // Calculate background styling for cell highlighting
    const getCellStyle = (record, colKey) => {
        if (record.isNew) {
            return { backgroundColor: '#f6ffed' }; // light green for new rows
        }

        const original = items.find(x => x.id === record.id);
        if (!original) return {};

        let isChanged = false;

        if (colKey === 'priceAmount') {
            const origPrice = original.price && original.price[0] ? original.price[0].price : null;
            const currentPrice = record.price && record.price[0] ? record.price[0].price : null;
            isChanged = origPrice !== currentPrice;
        } else if (colKey === 'expirationDate') {
            const origExp = original.price && original.price[0] ? original.price[0].expiration_date : null;
            const currentExp = record.price && record.price[0] ? record.price[0].expiration_date : null;

            const origTime = origExp
                ? (typeof origExp.toDate === 'function' ? origExp.toDate().getTime() : new Date(origExp).getTime())
                : null;
            const currentTime = currentExp
                ? (typeof currentExp.toDate === 'function' ? currentExp.toDate().getTime() : new Date(currentExp).getTime())
                : null;

            const oTime = isNaN(origTime) ? new Date('2099-12-01').getTime() : origTime;
            const cTime = isNaN(currentTime) ? new Date('2099-12-01').getTime() : currentTime;
            isChanged = oTime !== cTime;
        } else if (colKey === '取引日') {
            const origDate = original['取引日'];
            const currentDate = record['取引日'];

            const origTime = origDate
                ? (typeof origDate.toDate === 'function' ? origDate.toDate().getTime() : new Date(origDate).getTime())
                : null;
            const currentTime = currentDate
                ? (typeof currentDate.toDate === 'function' ? currentDate.toDate().getTime() : new Date(currentDate).getTime())
                : null;

            const oTime = isNaN(origTime) ? null : origTime;
            const cTime = isNaN(currentTime) ? null : currentTime;
            isChanged = oTime !== cTime;
        } else if (colKey === 'data') {
            const origData = original.data || [];
            const currentData = record.data || [];
            isChanged = JSON.stringify(origData) !== JSON.stringify(currentData);
        } else if (colKey === 'files') {
            const origFiles = original.files || [];
            const currentFiles = record.files || [];
            isChanged = JSON.stringify(origFiles) !== JSON.stringify(currentFiles);
        } else {
            isChanged = original[colKey] !== record[colKey];
        }

        return isChanged ? { backgroundColor: '#fff7e6', border: '1px solid #ffe7ba' } : {}; // light orange for changed cell
    };

    // Data editor actions inside Modal
    const handleDataCellClick = (record) => {
        setDataEditingRecordId(record.id);
        setEditingDataArray(record.data ? JSON.parse(JSON.stringify(record.data)) : []);
        setDataEditorOpen(true);
    };

    const handleDataArrayChange = (index, field, val) => {
        setEditingDataArray(prev => prev.map((item, idx) => {
            if (idx === index) {
                return { ...item, [field]: val };
            }
            return item;
        }));
    };

    const handleDataArrayDelete = (index) => {
        setEditingDataArray(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleAddDataPair = () => {
        setEditingDataArray([...editingDataArray, { title: '', value: '' }]);
    };

    const handleSaveDataArray = () => {
        const cleaned = editingDataArray.filter(x => x.title.trim() !== '' || x.value.trim() !== '');

        const updater = (prevRows) => prevRows.map(row => {
            if (row.id === dataEditingRecordId) {
                return { ...row, data: cleaned };
            }
            return row;
        });

        if (insertedRows.some(x => x.id === dataEditingRecordId)) {
            setInsertedRows(updater);
        } else {
            setLocalItems(updater);
        }
        setDataEditorOpen(false);
    };

    // Files/Attachments editor actions
    const handleFilesCellClick = (record) => {
        setFilesEditingRecordId(record.id);
        setEditingFilesArray(record.files ? JSON.parse(JSON.stringify(record.files)) : []);
        setFilesEditorOpen(true);
    };

    const handleFileRename = (index, newName) => {
        setEditingFilesArray(prev => prev.map((item, idx) => {
            if (idx === index) {
                return { ...item, name: newName };
            }
            return item;
        }));
    };

    const handleFileDelete = (index) => {
        setEditingFilesArray(prev => prev.filter((_, idx) => idx !== index));
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !storage) return;

        setUploadingFile(true);
        const storagePath = `manual/${Date.now()}_${file.name}`;
        const fileRef = storage.ref().child(storagePath);

        try {
            await fileRef.put(file);

            const lastDotIndex = file.name.lastIndexOf('.');
            const nameWithoutExtension = lastDotIndex !== -1 ? file.name.substring(0, lastDotIndex) : file.name;

            const fileDocRef = await firebase.firestore().collection('files').add({
                file: fileRef.location.path,
                name: nameWithoutExtension
            });

            const newFileItem = {
                file: fileDocRef.id,
                name: nameWithoutExtension
            };

            setEditingFilesArray(prev => [...prev, newFileItem]);
            message.success('ファイルをアップロードしました。');
        } catch (err) {
            console.error('File upload error:', err);
            message.error('ファイルのアップロードに失敗しました。');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleSaveFilesArray = () => {
        const cleanedFiles = editingFilesArray.filter(f => f.file && f.name);
        const cleanedFileNames = cleanedFiles.map(f => f.name.trim());

        const updater = (prevRows) => prevRows.map(row => {
            if (row.id === filesEditingRecordId) {
                return {
                    ...row,
                    files: cleanedFiles,
                    files_names: cleanedFileNames
                };
            }
            return row;
        });

        if (insertedRows.some(x => x.id === filesEditingRecordId)) {
            setInsertedRows(updater);
        } else {
            setLocalItems(updater);
        }
        setFilesEditorOpen(false);
    };

    // Image upload and management actions
    const handleImageCellClick = (record) => {
        setImageEditingRecordId(record.id);
        setEditingImageSrc(record.image || null);
        setImageEditorOpen(true);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !storage) return;

        setUploadingImage(true);
        const path = `items/${Date.now()}_${file.name}`;
        const fileRef = storage.ref().child(path);

        try {
            await fileRef.put(file);
            const gsUrl = `gs://${fileRef.location.bucket}/${fileRef.location.path}`;
            setEditingImageSrc(gsUrl);
            message.success('メイン画像をアップロードしました。');
        } catch (err) {
            console.error('Image upload error:', err);
            message.error('画像のアップロードに失敗しました。');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleClearImage = () => {
        setEditingImageSrc(null);
    };

    const handleSaveImageSrc = () => {
        const updater = (prevRows) => prevRows.map(row => {
            if (row.id === imageEditingRecordId) {
                return { ...row, image: editingImageSrc };
            }
            return row;
        });

        if (insertedRows.some(x => x.id === imageEditingRecordId)) {
            setInsertedRows(updater);
        } else {
            setLocalItems(updater);
        }
        setImageEditorOpen(false);
    };

    const getColumnSearchProps = (dataIndex, label, type = 'text') => {
        if (type === 'number_range') {
            return {
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <div style={{ marginBottom: 8 }}>
                            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
                                {label} 範囲指定
                            </Text>
                            <Space>
                                <InputNumber
                                    placeholder="最小"
                                    value={selectedKeys[0]?.[0]}
                                    onChange={val => {
                                        const current = selectedKeys[0] || [undefined, undefined];
                                        setSelectedKeys([[val, current[1]]]);
                                    }}
                                    style={{ width: 100 }}
                                />
                                <span>~</span>
                                <InputNumber
                                    placeholder="最大"
                                    value={selectedKeys[0]?.[1]}
                                    onChange={val => {
                                        const current = selectedKeys[0] || [undefined, undefined];
                                        setSelectedKeys([[current[0], val]]);
                                    }}
                                    style={{ width: 100 }}
                                />
                            </Space>
                        </div>
                        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button
                                type="primary"
                                onClick={() => confirm()}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 80 }}
                            >
                                検索
                            </Button>
                            <Button
                                onClick={() => {
                                    clearFilters();
                                    confirm();
                                }}
                                size="small"
                                style={{ width: 80 }}
                            >
                                リセット
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered) => (
                    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
                ),
                onFilter: (value, record) => {
                    let val = record[dataIndex];
                    if (dataIndex === 'priceAmount') {
                        val = record.price && record.price[0] ? record.price[0].price : null;
                    }
                    const [min, max] = value || [undefined, undefined];
                    if (min !== undefined && min !== null && (val === null || val === undefined || val < min)) return false;
                    if (max !== undefined && max !== null && (val === null || val === undefined || val > max)) return false;
                    return true;
                }
            };
        }

        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder={`${label}で検索`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button
                            type="primary"
                            onClick={() => confirm()}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            検索
                        </Button>
                        <Button
                            onClick={() => {
                                clearFilters();
                                confirm();
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            リセット
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value, record) => {
                const val = record[dataIndex];
                if (val === null || val === undefined) return false;
                return normalizeText(val).includes(normalizeText(value));
            }
        };
    };

    const renderHeaderTitle = (title, isRequired) => {
        return (
            <span>
                {title}
                {isRequired && <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>}
            </span>
        );
    };

    const baseColumns = [
        {
            title: renderHeaderTitle('No', true),
            dataIndex: 'No',
            key: 'No',
            fixed: 'left',
            sorter: (a, b) => Number(a.No || 0) - Number(b.No || 0),
            render: (value, record) => renderEditableCell(record, 'No', value, 'number')
        },
        {
            title: '画像',
            dataIndex: 'image',
            key: 'image',
            fixed: 'left',
            render: (value, record) => {
                const cellStyle = getCellStyle(record, 'image');
                return (
                    <ProductImageThumbnail
                        src={value}
                        storage={storage}
                        onClick={() => handleImageCellClick(record)}
                        cellStyle={cellStyle}
                    />
                );
            }
        },
        {
            title: renderHeaderTitle('P4CLOUD商品名', true),
            dataIndex: 'P4CLOUD商品名',
            key: 'P4CLOUD商品名',
            fixed: 'left',
            sorter: (a, b) => (a.P4CLOUD商品名 || '').localeCompare(b.P4CLOUD商品名 || '', 'ja'),
            ...getColumnSearchProps('P4CLOUD商品名', '商品名', 'text'),
            render: (value, record) => renderEditableCell(record, 'P4CLOUD商品名', value, 'text')
        },
        {
            title: renderHeaderTitle('価格 (円)', true),
            dataIndex: 'priceAmount',
            key: 'priceAmount',
            sorter: (a, b) => {
                const amtA = a.price && a.price[0] ? Number(a.price[0].price || 0) : 0;
                const amtB = b.price && b.price[0] ? Number(b.price[0].price || 0) : 0;
                return amtA - amtB;
            },
            ...getColumnSearchProps('priceAmount', '価格', 'number_range'),
            render: (value, record) => {
                const amt = record.price && record.price[0] ? record.price[0].price : null;
                return renderEditableCell(record, 'priceAmount', amt, 'number');
            }
        },
        {
            title: renderHeaderTitle('売価 (円)', true),
            dataIndex: '売価',
            key: '売価',
            sorter: (a, b) => Number(a.売価 || 0) - Number(b.売価 || 0),
            ...getColumnSearchProps('売価', '売価', 'number_range'),
            render: (value, record) => renderEditableCell(record, '売価', value, 'number')
        },
        {
            title: renderHeaderTitle('正式メーカー名', true),
            dataIndex: '正式メーカー名',
            key: '正式メーカー名',
            sorter: (a, b) => (a.正式メーカー名 || '').localeCompare(b.正式メーカー名 || '', 'ja'),
            ...getColumnSearchProps('正式メーカー名', '正式メーカー名', 'text'),
            render: (value, record) => renderEditableCell(record, '正式メーカー名', value, 'text')
        },
        {
            title: renderHeaderTitle('大分類', true),
            dataIndex: '大分類',
            key: '大分類',
            sorter: (a, b) => (a.大分類 || '').localeCompare(b.大分類 || '', 'ja'),
            ...getColumnSearchProps('大分類', '大分類', 'text'),
            render: (value, record) => renderEditableCell(record, '大分類', value, 'select', majorOptions)
        },
        {
            title: renderHeaderTitle('中分類', true),
            dataIndex: '中分類',
            key: '中分類',
            sorter: (a, b) => (a.中分類 || '').localeCompare(b.中分類 || '', 'ja'),
            ...getColumnSearchProps('中分類', '中分類', 'text'),
            render: (value, record) => {
                const opts = getMiddleOptions(record['大分類']);
                return renderEditableCell(record, '中分類', value, 'select', opts);
            }
        },
        {
            title: renderHeaderTitle('小分類', true),
            dataIndex: '小分類',
            key: '小分類',
            sorter: (a, b) => (a.小分類 || '').localeCompare(b.小分類 || '', 'ja'),
            ...getColumnSearchProps('小分類', '小分類', 'text'),
            render: (value, record) => {
                const opts = getSmallOptions(record['大分類'], record['中分類']);
                return renderEditableCell(record, '小分類', value, 'select', opts);
            }
        },
        {
            title: renderHeaderTitle('荷姿名称１', true),
            dataIndex: '荷姿名称１',
            key: '荷姿名称１',
            sorter: (a, b) => (a.荷姿名称１ || '').localeCompare(b.荷姿名称１ || '', 'ja'),
            ...getColumnSearchProps('荷姿名称１', '荷姿名称１', 'text'),
            render: (value, record) => renderEditableCell(record, '荷姿名称１', value, 'text')
        },
        {
            title: renderHeaderTitle('単位', true),
            dataIndex: '単位',
            key: '単位',
            sorter: (a, b) => (a.単位 || '').localeCompare(b.単位 || '', 'ja'),
            ...getColumnSearchProps('単位', '単位', 'text'),
            render: (value, record) => renderEditableCell(record, '単位', value, 'text')
        },
        {
            title: renderHeaderTitle('容量・入数', true),
            dataIndex: '容量・入数',
            key: '容量・入数',
            sorter: (a, b) => (a['容量・入数'] || '').localeCompare(b['容量・入数'] || '', 'ja'),
            ...getColumnSearchProps('容量・入数', '容量・入数', 'text'),
            render: (value, record) => renderEditableCell(record, '容量・入数', value, 'text')
        },
        {
            title: renderHeaderTitle('発注単位', true),
            dataIndex: '発注単位',
            key: '発注単位',
            sorter: (a, b) => (a.発注単位 || '').localeCompare(b.発注単位 || '', 'ja'),
            ...getColumnSearchProps('発注単位', '発注単位', 'text'),
            render: (value, record) => renderEditableCell(record, '発注単位', value, 'text')
        },
        {
            title: renderHeaderTitle('発注数', true),
            dataIndex: '発注数',
            key: '発注数',
            sorter: (a, b) => Number(a.発注数 || 0) - Number(b.発注数 || 0),
            ...getColumnSearchProps('発注数', '発注数', 'number_range'),
            render: (value, record) => renderEditableCell(record, '発注数', value, 'number')
        },
        {
            title: renderHeaderTitle('発注数量', true),
            dataIndex: '発注数量',
            key: '発注数量',
            sorter: (a, b) => Number(a.発注数量 || 0) - Number(b.発注数量 || 0),
            ...getColumnSearchProps('発注数量', '発注数量', 'number_range'),
            render: (value, record) => renderEditableCell(record, '発注数量', value, 'number')
        },
        {
            title: renderHeaderTitle('消費税率', true),
            dataIndex: '消費税率',
            key: '消費税率',
            sorter: (a, b) => Number(a.消費税率 || 0) - Number(b.消費税率 || 0),
            render: (value, record) => renderEditableCell(record, '消費税率', value, 'select', TAX_OPTIONS)
        },
        {
            title: renderHeaderTitle('正式メーカー商品名', true),
            dataIndex: '正式メーカー商品名',
            key: '正式メーカー商品名',
            sorter: (a, b) => (a.正式メーカー商品名 || '').localeCompare(b.正式メーカー商品名 || '', 'ja'),
            ...getColumnSearchProps('正式メーカー商品名', '正式メーカー商品名', 'text'),
            render: (value, record) => renderEditableCell(record, '正式メーカー商品名', value, 'text')
        },
        {
            title: renderHeaderTitle('仕入先（本社）', true),
            dataIndex: '仕入先（本社）',
            key: '仕入先（本社）',
            sorter: (a, b) => (a['仕入先（本社）'] || '').localeCompare(b['仕入先（本社）'] || '', 'ja'),
            ...getColumnSearchProps('仕入先（本社）', '仕入先（本社）', 'text'),
            render: (value, record) => renderEditableCell(record, '仕入先（本社）', value, 'text')
        },
        {
            title: renderHeaderTitle('仕入先事業所名', true),
            dataIndex: '仕入先事業所名',
            key: '仕入先事業所名',
            sorter: (a, b) => (a.仕入先事業所名 || '').localeCompare(b.仕入先事業所名 || '', 'ja'),
            ...getColumnSearchProps('仕入先事業所名', '仕入先事業所名', 'text'),
            render: (value, record) => renderEditableCell(record, '仕入先事業所名', value, 'text')
        },
        {
            title: 'P4CLOUD商品名ふりがな',
            dataIndex: 'P4CLOUD商品名ふりがな',
            key: 'P4CLOUD商品名ふりがな',
            sorter: (a, b) => (a.P4CLOUD商品名ふりがな || '').localeCompare(b.P4CLOUD商品名ふりがな || '', 'ja'),
            ...getColumnSearchProps('P4CLOUD商品名ふりがな', 'ふりがな', 'text'),
            render: (value, record) => renderEditableCell(record, 'P4CLOUD商品名ふりがな', value, 'text')
        },
        {
            title: '色',
            dataIndex: '色',
            key: '色',
            sorter: (a, b) => (a.色 || '').localeCompare(b.色 || '', 'ja'),
            render: (value, record) => renderEditableCell(record, '色', value, 'text')
        },
        {
            title: '容量',
            dataIndex: '容量',
            key: '容量',
            sorter: (a, b) => (a.容量 || '').localeCompare(b.容量 || '', 'ja'),
            render: (value, record) => renderEditableCell(record, '容量', value, 'text')
        },
        {
            title: '分類',
            dataIndex: '分類',
            key: '分類',
            sorter: (a, b) => (a.分類 || '').localeCompare(b.分類 || '', 'ja'),
            render: (value, record) => renderEditableCell(record, '分類', value, 'text')
        },
        {
            title: '劇毒物区分名',
            dataIndex: '劇毒物区分名',
            key: '劇毒物区分名',
            sorter: (a, b) => (a.劇毒物区分名 || '').localeCompare(b.劇毒物区分名 || '', 'ja'),
            render: (value, record) => renderEditableCell(record, '劇毒物区分名', value, 'text')
        },
        {
            title: '危険物区分名',
            dataIndex: '危険物区分名',
            key: '危険物区分名',
            sorter: (a, b) => (a.危険物区分名 || '').localeCompare(b.危険物区分名 || '', 'ja'),
            render: (value, record) => renderEditableCell(record, '危険物区分名', value, 'text')
        },
        {
            title: '取引日',
            dataIndex: '取引日',
            key: '取引日',
            sorter: (a, b) => {
                const dateA = a['取引日'] ? (typeof a['取引日'].toDate === 'function' ? a['取引日'].toDate() : new Date(a['取引日'])) : new Date(0);
                const dateB = b['取引日'] ? (typeof b['取引日'].toDate === 'function' ? b['取引日'].toDate() : new Date(b['取引日'])) : new Date(0);
                return dateA - dateB;
            },
            render: (value, record) => {
                const dateObj = value ? (typeof value.toDate === 'function' ? value.toDate() : new Date(value)) : null;
                return renderEditableCell(record, '取引日', dateObj, 'date');
            }
        },
        {
            title: renderHeaderTitle('価格有効期限', true),
            dataIndex: 'expirationDate',
            key: 'expirationDate',
            sorter: (a, b) => {
                const rawA = a.price && a.price[0]?.expiration_date;
                const rawB = b.price && b.price[0]?.expiration_date;
                const dateA = rawA ? (typeof rawA.toDate === 'function' ? rawA.toDate() : new Date(rawA)) : new Date('2099-12-01');
                const dateB = rawB ? (typeof rawB.toDate === 'function' ? rawB.toDate() : new Date(rawB)) : new Date('2099-12-01');
                return dateA - dateB;
            },
            render: (value, record) => {
                const rawExp = record.price && record.price[0]?.expiration_date;
                let dateObj = null;
                if (rawExp) {
                    if (typeof rawExp.toDate === 'function') {
                        dateObj = rawExp.toDate();
                    } else {
                        dateObj = new Date(rawExp);
                    }
                }
                if (!dateObj || isNaN(dateObj.getTime())) {
                    dateObj = new Date('2099-12-01');
                }
                return renderEditableCell(record, 'expirationDate', dateObj, 'date');
            }
        },
        {
            title: 'アーカイブ',
            dataIndex: 'archive',
            key: 'archive',
            sorter: (a, b) => (a.archive === true ? 1 : 0) - (b.archive === true ? 1 : 0),
            render: (value, record) => {
                const displayVal = value === true;
                return renderEditableCell(record, 'archive', displayVal, 'select', [
                    { label: 'アクティブ (通常)', value: false },
                    { label: 'アーカイブ (非表示)', value: true }
                ]);
            }
        },
        {
            title: '仕様データ',
            dataIndex: 'data',
            key: 'data',
            render: (value, record) => {
                const count = value ? value.length : 0;
                const cellStyle = getCellStyle(record, 'data');
                return (
                    <div
                        onClick={() => handleDataCellClick(record)}
                        className="data-array-cell"
                        style={{
                            cursor: 'pointer',
                            padding: '4px',
                            background: '#f5f5f5',
                            borderRadius: '4px',
                            textAlign: 'center',
                            transition: 'background 0.3s',
                            ...cellStyle
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8e8e8'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cellStyle.backgroundColor || '#f5f5f5'}
                    >
                        <span>仕様データ: {count} 件</span>
                    </div>
                );
            }
        },
        {
            title: '添付ファイル',
            dataIndex: 'files',
            key: 'files',
            render: (value, record) => {
                const count = value ? value.length : 0;
                const cellStyle = getCellStyle(record, 'files');
                return (
                    <div
                        onClick={() => handleFilesCellClick(record)}
                        className="files-array-cell"
                        style={{
                            cursor: 'pointer',
                            padding: '4px',
                            background: '#f5f5f5',
                            borderRadius: '4px',
                            textAlign: 'center',
                            transition: 'background 0.3s',
                            ...cellStyle
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8e8e8'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cellStyle.backgroundColor || '#f5f5f5'}
                    >
                        <span>添付ファイル: {count} 件</span>
                    </div>
                );
            }
        },
        {
            title: '売単価 (円)',
            dataIndex: '売単価',
            key: '売単価',
            sorter: (a, b) => Number(a.売単価 || 0) - Number(b.売単価 || 0),
            ...getColumnSearchProps('売単価', '売単価', 'number_range'),
            render: (value, record) => renderEditableCell(record, '売単価', value, 'number')
        },
        {
            title: 'メーカー商品コード',
            dataIndex: 'メーカー商品コード',
            key: 'メーカー商品コード',
            sorter: (a, b) => (a.メーカー商品コード || '').localeCompare(b.メーカー商品コード || '', 'ja'),
            ...getColumnSearchProps('メーカー商品コード', 'メーカー商品コード', 'text'),
            render: (value, record) => renderEditableCell(record, 'メーカー商品コード', value, 'text')
        },
        {
            title: 'メーカー法人番号',
            dataIndex: 'メーカー法人番号',
            key: 'メーカー法人番号',
            sorter: (a, b) => (a.メーカー法人番号 || '').localeCompare(b.メーカー法人番号 || '', 'ja'),
            ...getColumnSearchProps('メーカー法人番号', 'メーカー法人番号', 'text'),
            render: (value, record) => renderEditableCell(record, 'メーカー法人番号', value, 'text')
        },
        {
            title: '仕入先事業所コード',
            dataIndex: '仕入先事業所コード',
            key: '仕入先事業所コード',
            sorter: (a, b) => (a.仕入先事業所コード || '').localeCompare(b.仕入先事業所コード || '', 'ja'),
            ...getColumnSearchProps('仕入先事業所コード', '仕入先事業所コード', 'text'),
            render: (value, record) => renderEditableCell(record, '仕入先事業所コード', value, 'text')
        },
        {
            title: '仕入先法人番号',
            dataIndex: '仕入先法人番号',
            key: '仕入先法人番号',
            sorter: (a, b) => (a.仕入先法人番号 || '').localeCompare(b.仕入先法人番号 || '', 'ja'),
            ...getColumnSearchProps('仕入先法人番号', '仕入先法人番号', 'text'),
            render: (value, record) => renderEditableCell(record, '仕入先法人番号', value, 'text')
        },
        {
            title: '仕入先EDIコード商品コード',
            dataIndex: '仕入先EDIコード商品コード',
            key: '仕入先EDIコード商品コード',
            sorter: (a, b) => (a.仕入先EDIコード商品コード || '').localeCompare(b.仕入先EDIコード商品コード || '', 'ja'),
            ...getColumnSearchProps('仕入先EDIコード商品コード', '仕入先EDIコード商品コード', 'text'),
            render: (value, record) => renderEditableCell(record, '仕入先EDIコード商品コード', value, 'text')
        },
        {
            title: '仕入先発注コード',
            dataIndex: '仕入先発注コード',
            key: '仕入先発注コード',
            sorter: (a, b) => (a.仕入先発注コード || '').localeCompare(b.仕入先発注コード || '', 'ja'),
            ...getColumnSearchProps('仕入先発注コード', '仕入先発注コード', 'text'),
            render: (value, record) => renderEditableCell(record, '仕入先発注コード', value, 'text')
        },
        {
            title: '販売企業商品コード',
            dataIndex: '販売企業商品コード',
            key: '販売企業商品コード',
            sorter: (a, b) => (a.販売企業商品コード || '').localeCompare(b.販売企業商品コード || '', 'ja'),
            ...getColumnSearchProps('販売企業商品コード', '販売企業商品コード', 'text'),
            render: (value, record) => renderEditableCell(record, '販売企業商品コード', value, 'text')
        }
    ];

    const actionColumn = {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 100,
        render: (text, record) => (
            <Popconfirm
                title="この商品を削除しますか？"
                onConfirm={() => handleDeleteRow(record.id)}
                okText="はい"
                cancelText="いいえ"
            >
                <Button type="link" danger icon={<DeleteOutlined />}>
                    削除
                </Button>
            </Popconfirm>
        )
    };

    // Dynamic columns configuration with custom order, visibility, and resize handles
    const columns = useMemo(() => {
        const ordered = [];

        // 1. Map other columns based on columnOrder and visibleColumns
        columnOrder.forEach((key, idx) => {
            if (!visibleColumns.includes(key)) return;
            const col = baseColumns.find(c => c.key === key);
            if (col) {
                const isFixedLeft = col.fixed === 'left' && idx < 3;
                ordered.push({
                    ...col,
                    fixed: isFixedLeft ? 'left' : undefined,
                    width: colWidths[key] || col.width || 120
                });
            }
        });

        // 2. Add the action column fixed at the right
        // ordered.push(actionColumn);

        // Map resize functionality
        return ordered.map(col => {
            if (col.key === 'action') {
                return col;
            }
            return {
                ...col,
                title: (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', width: '100%' }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.title}</span>
                        <div
                            onMouseDown={handleResize(col.key)}
                            style={{
                                position: 'absolute',
                                right: -8,
                                top: -8,
                                bottom: -8,
                                width: 10,
                                cursor: 'col-resize',
                                zIndex: 100,
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.borderRight = '2px solid #1890ff'}
                            onMouseLeave={(e) => e.currentTarget.style.borderRight = 'none'}
                        />
                    </div>
                )
            };
        });
    }, [columnOrder, visibleColumns, colWidths, majorOptions, editingCell, editValue, items, localItems, insertedRows]);

    const renderEditableCell = (record, colKey, value, type, selectOptions = []) => {
        const isEditing = editingCell && editingCell.rowId === record.id && editingCell.colKey === colKey;
        const cellStyle = getCellStyle(record, colKey);

        if (isEditing) {
            if (type === 'number') {
                return (
                    <InputNumber
                        value={editValue}
                        onChange={setEditValue}
                        onBlur={() => handleLocalCellSave(record.id, colKey, editValue)}
                        onPressEnter={() => handleLocalCellSave(record.id, colKey, editValue)}
                        autoFocus
                        style={{ width: '100%' }}
                    />
                );
            }
            if (type === 'select') {
                return (
                    <Select
                        value={editValue}
                        onChange={(val) => {
                            setEditValue(val);
                            handleLocalCellSave(record.id, colKey, val);
                        }}
                        onBlur={() => setEditingCell(null)}
                        autoFocus
                        open
                        style={{ width: '100%' }}
                        options={selectOptions}
                    />
                );
            }
            if (type === 'date') {
                return (
                    <DatePicker
                        value={editValue ? dayjs(editValue) : null}
                        onChange={(val) => {
                            const dateVal = val ? val.toDate() : null;
                            setEditValue(dateVal);
                            handleLocalCellSave(record.id, colKey, dateVal);
                        }}
                        onOpenChange={(open) => {
                            if (!open) {
                                setTimeout(() => {
                                    setEditingCell(null);
                                }, 150);
                            }
                        }}
                        autoFocus
                        defaultOpen
                        style={{ width: '100%' }}
                    />
                );
            }
            return (
                <Input
                    value={editValue || ''}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => handleLocalCellSave(record.id, colKey, editValue)}
                    onPressEnter={() => handleLocalCellSave(record.id, colKey, editValue)}
                    autoFocus
                    style={{ width: '100%' }}
                />
            );
        }

        // Render formatted display values
        let displayValue = value;
        if (type === 'date' && value) {
            if (value instanceof Date && !isNaN(value.getTime())) {
                displayValue = dayjs(value).format('YYYY-MM-DD');
            } else {
                displayValue = '2099-12-01';
            }
        } else if (type === 'select' && selectOptions.length > 0) {
            const matched = selectOptions.find(opt => opt.value === value);
            displayValue = matched ? matched.label : value;
        } else if (typeof value === 'number') {
            displayValue = isNaN(value) ? '' : value.toLocaleString();
        }

        return (
            <div
                onClick={() => handleCellClick(record, colKey, value)}
                className="editable-cell-value"
                style={{
                    cursor: 'pointer',
                    minHeight: '24px',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'background 0.3s',
                    ...cellStyle
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = record.isNew ? '#e6f7ff' : '#f0f2f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = cellStyle.backgroundColor || 'transparent'}
            >
                {displayValue !== null && displayValue !== undefined && displayValue !== '' ? (
                    displayValue
                ) : (
                    <span style={{ color: '#ccc', fontStyle: 'italic' }}>未設定</span>
                )}
            </div>
        );
    };

    return (
        <Card
            title={
                <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Title level={3} style={{ margin: 0 }}>商品一括管理 (管理者用)</Title>
                    <Space size="middle">
                        <Input
                            placeholder="商品名、メーカー名で検索"
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 250, height: 38 }}
                            allowClear
                        />
                        <ProductExcelImport
                            getWriteRef={() => getCollectionRef(db, org, orgSeller)}
                            onSuccessRefresh={fetchItems}
                            existingItems={items}
                            sellerOrg={orgSeller}
                            buyerOrg={org}
                        />
                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={handleAddRow}
                            style={{ height: 38 }}
                        >
                            新規行を追加
                        </Button>
                    </Space>
                </Space>
            }
            style={{ margin: '16px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
        >
            {hasUnsavedChanges && (
                <Alert
                    message={
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <span>
                                <strong>未保存の変更があります。</strong>
                            </span>
                            <Space>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<SaveOutlined />}
                                    onClick={handleTriggerSave}
                                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                >
                                    変更を保存
                                </Button>
                                <Button
                                    danger
                                    size="small"
                                    icon={<UndoOutlined />}
                                    onClick={handleDiscardChanges}
                                >
                                    変更を破棄
                                </Button>
                            </Space>
                        </Space>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {!org || !orgSeller ? (
                <Alert
                    message="組織が選択されていません"
                    description="ホーム画面またはヘッダーから所属・相手先事業所を正しく選択してください。"
                    type="warning"
                    showIcon
                />
            ) : (
                <>
                    <Row justify="space-between" align="middle" style={{ marginBottom: 16, gap: 12 }}>
                        <Col>
                            <Radio.Group
                                value={currentTab}
                                onChange={e => {
                                    setCurrentTab(e.target.value);
                                    setSelectedRowKeys([]); // Reset checkboxes on tab change
                                }}
                                buttonStyle="solid"
                            >
                                <Radio.Button value="active">通常商品</Radio.Button>
                                <Radio.Button value="archived">アーカイブ</Radio.Button>
                                <Radio.Button value="expired">価格有効期限切れ</Radio.Button>
                            </Radio.Group>

                            {currentTab === 'expired' && (
                                <DatePicker.RangePicker
                                    value={expiredDateRange}
                                    onChange={setExpiredDateRange}
                                    style={{ marginLeft: 16 }}
                                    placeholder={['有効期限 開始', '有効期限 終了']}
                                />
                            )}
                        </Col>
                        <Col>
                            <Space size="middle">
                                {selectedRowKeys.length > 0 && (
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={() => {
                                            setBulkEditField('');
                                            setBulkEditValue(null);
                                            setBulkEditOpen(true);
                                        }}
                                        style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                                    >
                                        選択行を一括編集 ({selectedRowKeys.length}件)
                                    </Button>
                                )}
                                <Button
                                    icon={<SettingOutlined />}
                                    onClick={() => setColumnSettingsOpen(true)}
                                >
                                    表示列の設定
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table
                        rowSelection={{
                            selectedRowKeys,
                            onChange: setSelectedRowKeys
                        }}
                        columns={columns}
                        dataSource={filteredItems}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50, 100],
                            showTotal: (total, range) => `${total}件中 ${range[0]}-${range[1]}件`,
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            }
                        }}
                        bordered
                        size="small"
                        locale={{
                            triggerAsc: 'クリックで昇順に並べ替え',
                            triggerDesc: 'クリックで降順に並べ替え',
                            cancelSort: 'クリックで並べ替えを解除'
                        }}
                    />
                </>
            )}

            {/* Specifications (Data Array) Modal Editor */}
            <Modal
                title="仕様データ編集"
                open={dataEditorOpen}
                onCancel={() => setDataEditorOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setDataEditorOpen(false)}>
                        キャンセル
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveDataArray}>
                        決定
                    </Button>
                ]}
                width={600}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">商品の仕様やカスタム属性（キーと値のペア）を入力してください。</Text>
                </div>

                {editingDataArray.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', background: '#fafafa', borderRadius: '4px', marginBottom: 16 }}>
                        <Text type="secondary">登録されている仕様データはありません。</Text>
                    </div>
                ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: 16, paddingRight: 8 }}>
                        {editingDataArray.map((item, index) => (
                            <Row gutter={8} key={index} style={{ marginBottom: 8, alignItems: 'center' }}>
                                <Col span={10}>
                                    <Input
                                        placeholder="項目名 (例: 材質, 重さ)"
                                        value={item.title}
                                        onChange={(e) => handleDataArrayChange(index, 'title', e.target.value)}
                                    />
                                </Col>
                                <Col span={11}>
                                    <Input
                                        placeholder="値 (例: ステンレス, 1.2kg)"
                                        value={item.value}
                                        onChange={(e) => handleDataArrayChange(index, 'value', e.target.value)}
                                    />
                                </Col>
                                <Col span={3} style={{ textAlign: 'right' }}>
                                    <Popconfirm
                                        title="この項目を削除しますか？"
                                        onConfirm={() => handleDataArrayDelete(index)}
                                        okText="はい"
                                        cancelText="いいえ"
                                    >
                                        <Button type="link" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </Col>
                            </Row>
                        ))}
                    </div>
                )}

                <Button
                    type="dashed"
                    onClick={handleAddDataPair}
                    block
                    icon={<PlusOutlined />}
                >
                    項目を追加
                </Button>
            </Modal>

            {/* Files/Attachments Modal Editor */}
            <Modal
                title="添付ファイル編集"
                open={filesEditorOpen}
                onCancel={() => setFilesEditorOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setFilesEditorOpen(false)}>
                        キャンセル
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveFilesArray}>
                        決定
                    </Button>
                ]}
                width={600}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">取扱説明書やカタログなどの添付ファイルを管理します。</Text>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16, maxHeight: '350px', overflowY: 'auto', paddingRight: 8 }}>
                    {editingFilesArray.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '24px', background: '#fafafa', borderRadius: 4 }}>
                            <Text type="secondary">添付されているファイルはありません。</Text>
                        </div>
                    ) : (
                        editingFilesArray.map((item, index) => (
                            <FileAttachmentItem
                                key={index}
                                fileId={item.file}
                                name={item.name}
                                onRename={(newName) => handleFileRename(index, newName)}
                                onDelete={() => handleFileDelete(index)}
                                storage={storage}
                            />
                        ))
                    )}
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                        type="file"
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                        id="grid-attachment-file-upload"
                    />
                    <Button
                        type="dashed"
                        block
                        loading={uploadingFile}
                        icon={<PaperClipOutlined />}
                        onClick={() => document.getElementById('grid-attachment-file-upload').click()}
                    >
                        新規ファイルをアップロードして追加
                    </Button>
                </div>
            </Modal>

            {/* Product Image Modal Editor */}
            <Modal
                title="メイン画像編集"
                open={imageEditorOpen}
                onCancel={() => setImageEditorOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setImageEditorOpen(false)}>
                        キャンセル
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveImageSrc}>
                        決定
                    </Button>
                ]}
                width={400}
                destroyOnClose
            >
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                    <Text type="secondary">商品の一覧や詳細画面に表示されるメイン画像を設定します。</Text>
                </div>

                <ProductImageLargePreview src={editingImageSrc} storage={storage} />

                <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        id="grid-image-file-upload"
                    />
                    <Button
                        type="primary"
                        ghost
                        loading={uploadingImage}
                        icon={<PictureOutlined />}
                        onClick={() => document.getElementById('grid-image-file-upload').click()}
                    >
                        新画像アップロード
                    </Button>
                    {editingImageSrc && (
                        <Button danger icon={<DeleteOutlined />} onClick={handleClearImage}>
                            画像をクリア
                        </Button>
                    )}
                </div>
            </Modal>

            {/* Changes Confirmation Modal */}
            <Modal
                title="変更内容の確認"
                open={confirmSaveOpen}
                onCancel={() => setConfirmSaveOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setConfirmSaveOpen(false)}>
                        キャンセル
                    </Button>,
                    <Button key="save" type="primary" onClick={handleSaveChanges} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                        決定して保存
                    </Button>
                ]}
                width={650}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>以下の変更内容をデータベースに保存します。よろしいですか？</Text>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: 8 }}>
                    {changeSummary.inserted.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                            <Title level={5} style={{ color: '#52c41a', marginTop: 0 }}>【新規追加: {changeSummary.inserted.length} 件】</Title>
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {changeSummary.inserted.map((row, idx) => (
                                    <li key={idx}>No.{row.No} - <Text strong>{row.name}</Text></li>
                                ))}
                            </ul>
                            <Divider style={{ margin: '12px 0' }} />
                        </div>
                    )}

                    {changeSummary.modified.length > 0 && (
                        <div>
                            <Title level={5} style={{ color: '#fa8c16', marginTop: 0 }}>【変更: {changeSummary.modified.length} 件】</Title>
                            {changeSummary.modified.map((row, idx) => (
                                <div key={idx} style={{ marginBottom: 16, padding: '12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
                                    <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 8 }}>No.{row.No} - {row.name}</Text>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {row.changes.map((c, cIdx) => (
                                            <li key={cIdx} style={{ marginBottom: 4 }}>
                                                <Text type="secondary">{c.label}:</Text> <span style={{ textDecoration: 'line-through', color: '#8c8c8c', marginRight: 4 }}>{c.old}</span> <span style={{ color: '#fa8c16', marginRight: 4 }}>→</span> <Text strong style={{ color: '#f5222d' }}>{c.new}</Text>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Column Settings Modal */}
            <Modal
                title="表示列の並び替え・表示切り替え"
                open={columnSettingsOpen}
                onCancel={() => setColumnSettingsOpen(false)}
                footer={[
                    <Button key="reset" onClick={() => {
                        setColumnOrder(DEFAULT_COLUMN_ORDER);
                        setVisibleColumns(DEFAULT_COLUMN_ORDER);
                        saveLayout(DEFAULT_COLUMN_ORDER, DEFAULT_COLUMN_ORDER, colWidths);
                        message.success('表示列の設定を初期値にリセットしました。');
                    }}>
                        初期値に戻す
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setColumnSettingsOpen(false)}>
                        閉じる
                    </Button>
                ]}
                width={500}
                destroyOnClose
            >
                <div style={{ marginBottom: 12 }}>
                    <Text type="secondary">項目をドラッグ、または矢印ボタンで順序を並び替え、チェックボックスで表示・非表示を切り替えられます。</Text>
                </div>
                <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: 8 }}>
                    {columnOrder.map((key, index) => {
                        const col = baseColumns.find(c => c.key === key);
                        if (!col) return null;

                        let labelText = FIELD_LABELS[key] || key;
                        if (key === 'priceAmount') labelText = '価格 (円)';

                        const isVisible = visibleColumns.includes(key);

                        return (
                            <div
                                key={key}
                                draggable
                                onDragStart={(e) => handleColumnDragStart(e, index)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => handleColumnDrop(e, index)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 12px',
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '6px',
                                    marginBottom: '6px',
                                    backgroundColor: '#fafafa',
                                    cursor: 'grab',
                                    transition: 'border-color 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#40a9ff'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#f0f0f0'}
                            >
                                <Space>
                                    <MenuOutlined style={{ color: '#bfbfbf', cursor: 'grab' }} />
                                    <Checkbox
                                        checked={isVisible}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            let nextVisible;
                                            if (checked) {
                                                nextVisible = [...visibleColumns, key];
                                            } else {
                                                nextVisible = visibleColumns.filter(k => k !== key);
                                            }
                                            setVisibleColumns(nextVisible);
                                            saveLayout(columnOrder, nextVisible, colWidths);
                                        }}
                                    >
                                        {labelText}
                                    </Checkbox>
                                </Space>
                                <Space>
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<ArrowUpOutlined />}
                                        disabled={index === 0}
                                        onClick={() => {
                                            const newOrder = [...columnOrder];
                                            const temp = newOrder[index - 1];
                                            newOrder[index - 1] = newOrder[index];
                                            newOrder[index] = temp;
                                            setColumnOrder(newOrder);
                                            saveLayout(newOrder, visibleColumns, colWidths);
                                        }}
                                    />
                                    <Button
                                        size="small"
                                        type="text"
                                        icon={<ArrowDownOutlined />}
                                        disabled={index === columnOrder.length - 1}
                                        onClick={() => {
                                            const newOrder = [...columnOrder];
                                            const temp = newOrder[index + 1];
                                            newOrder[index + 1] = newOrder[index];
                                            newOrder[index] = temp;
                                            setColumnOrder(newOrder);
                                            saveLayout(newOrder, visibleColumns, colWidths);
                                        }}
                                    />
                                </Space>
                            </div>
                        );
                    })}
                </div>
            </Modal>

            {/* Bulk Edit Modal */}
            <Modal
                title="選択した商品を一括編集"
                open={bulkEditOpen}
                onCancel={() => setBulkEditOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setBulkEditOpen(false)}>
                        キャンセル
                    </Button>,
                    <Button key="apply" type="primary" onClick={handleApplyBulkEdit}>
                        選択行に適用
                    </Button>
                ]}
                width={500}
                destroyOnClose
            >
                <div style={{ marginBottom: 16 }}>
                    <Text type="warning" strong>選択された {selectedRowKeys.length} 件の商品に対して、一括で新しい値を設定します。</Text>
                </div>
                <Form layout="vertical">
                    <Form.Item label="編集する項目を選択">
                        <Select
                            placeholder="一括編集する列を選択してください"
                            value={bulkEditField}
                            onChange={(val) => {
                                setBulkEditField(val);
                                setBulkEditValue(null);
                            }}
                            options={[
                                { label: '大分類', value: '大分類' },
                                { label: '中分類', value: '中分類' },
                                { label: '小分類', value: '小分類' },
                                { label: '正式メーカー名', value: '正式メーカー名' },
                                { label: '仕入先（本社）', value: '仕入先（本社）' },
                                { label: '価格 (価格Amount)', value: 'priceAmount' },
                                { label: '売価 (円)', value: '売価' },
                                { label: '売単価 (円)', value: '売単価' },
                                { label: '発注単位', value: '発注単位' },
                                { label: '発注数', value: '発注数' },
                                { label: '発注数量', value: '発注数量' },
                                { label: '消費税率', value: '消費税率' },
                                { label: '価格有効期限', value: 'expirationDate' },
                                { label: 'アーカイブ', value: 'archive' }
                            ]}
                        />
                    </Form.Item>

                    {bulkEditField && (
                        <Form.Item label="新しい設定値">
                            {bulkEditField === '大分類' && (
                                <Select
                                    value={bulkEditValue}
                                    onChange={setBulkEditValue}
                                    options={majorOptions}
                                    placeholder="大分類を選択"
                                />
                            )}
                            {bulkEditField === '中分類' && (
                                <Select
                                    value={bulkEditValue}
                                    onChange={setBulkEditValue}
                                    options={treeData.filter(item => item.level === 'middle').map(item => ({ label: item.name2, value: item.code }))}
                                    placeholder="中分類を選択"
                                />
                            )}
                            {bulkEditField === '小分類' && (
                                <Select
                                    value={bulkEditValue}
                                    onChange={setBulkEditValue}
                                    options={treeData.filter(item => item.level === 'small').map(item => ({ label: item.name2, value: item.code }))}
                                    placeholder="小分類を選択"
                                />
                            )}
                            {['priceAmount', '売価', '売単価', '発注数', '発注数量'].includes(bulkEditField) && (
                                <InputNumber
                                    value={bulkEditValue}
                                    onChange={setBulkEditValue}
                                    style={{ width: '100%' }}
                                    placeholder="数値を入力"
                                />
                            )}
                            {bulkEditField === '消費税率' && (
                                <Select
                                    value={bulkEditValue}
                                    onChange={setBulkEditValue}
                                    options={TAX_OPTIONS}
                                    placeholder="消費税率を選択"
                                />
                            )}
                            {bulkEditField === 'expirationDate' && (
                                <DatePicker
                                    value={bulkEditValue ? dayjs(bulkEditValue) : null}
                                    onChange={(val) => setBulkEditValue(val ? val.toDate() : null)}
                                    style={{ width: '100%' }}
                                    placeholder="日付を選択"
                                />
                            )}
                            {bulkEditField === 'archive' && (
                                <Select
                                    value={bulkEditValue}
                                    onChange={setBulkEditValue}
                                    options={[
                                        { label: 'アクティブ (通常)', value: false },
                                        { label: 'アーカイブ (非表示)', value: true }
                                    ]}
                                    placeholder="アーカイブステータスを選択"
                                />
                            )}
                            {!['大分類', '中分類', '小分類', 'priceAmount', '売価', '売単価', '発注数', '発注数量', '消費税率', 'expirationDate', 'archive'].includes(bulkEditField) && (
                                <Input
                                    value={bulkEditValue || ''}
                                    onChange={(e) => setBulkEditValue(e.target.value)}
                                    placeholder="文字列を入力"
                                />
                            )}
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </Card>
    );
};

export default ProductManagement;
